import type { BugSmashConfig } from './types.js';
import { resolveWidgetConfig } from './resolveConfig.js';
import { Widget } from './ui/Widget.js';

let activeWidget: Widget | null = null;

/**
 * Initializes the BugSmash widget and attaches the floating button to the page.
 *
 * @example
 * ```js
 * import { BugSmash } from 'bugsmash-widget';
 * // or via <script> tag: window.BugSmash.init(...)
 *
 * await BugSmash.init({
 *   projectId: 'your-project-id',
 *   version: 2,
 * });
 *
 * await BugSmash.init({
 *   projectId: 'your-project-id',
 *   version: 'Release candidate',
 * });
 * ```
 */
export async function init(config: BugSmashConfig): Promise<void> {
  if (activeWidget) {
    console.warn('[BugSmash] Widget already initialized. Call destroy() first.');
    return;
  }

  if (!config.projectId) throw new Error('[BugSmash] projectId is required');

  const resolved = await resolveWidgetConfig(config);
  activeWidget = new Widget(resolved);
}

/**
 * Removes the BugSmash widget from the page.
 */
export function destroy(): void {
  activeWidget?.destroy();
  activeWidget = null;
}

// Named export object — works for both `import { BugSmash }` and `window.BugSmash`
export const BugSmash = { init, destroy };

export default BugSmash;
